/**
 * Auth context instance + its types/initial state.
 *
 * IMPORTANT — why createContext lives here and not in Auth.tsx:
 *
 * Auth.tsx exports components (AuthProvider, AuthInit) AND a hook (useAuth), so it is not a clean
 * React Fast Refresh boundary. During development, saving Auth.tsx (or a file it depends on)
 * re-executes the module; if `createContext` lived there it would mint a new context object on
 * every such edit. The <AuthContext.Provider> would keep the old object while consumers read the
 * new one and fall back to `initAuthContextPropsState` (currentUser: undefined). Because
 * AbilityProvider derives the CASL ability from `useAuth().currentUser`, that fallback empties the
 * ability and makes permission-gated UI (e.g. sidebar items) disappear until a full reload.
 *
 * Keeping the context instance in this dependency-light module keeps its identity stable across
 * HMR, so the provider and consumers always share the same context object.
 */
import { createContext, type Dispatch, type SetStateAction } from 'react';
import { type AccountDetailsDto } from '../../../models/AccountDetailsDto';
import { type AuthModel } from './_models';
import * as authHelper from './AuthHelpers';

export type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: AccountDetailsDto | undefined;
  setCurrentUser: Dispatch<SetStateAction<AccountDetailsDto | undefined>>;
  logout: (errorMessage?: string) => void;
  errorMessage?: string;
};

export const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  errorMessage: undefined,
};

export const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);
